"use client";

import { useState } from "react";
import { heroMilestones } from "@/lib/orplanet/data";

export function HeroChart() {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const maxFleet = 760;
    const chartWidth = 420;
    const chartHeight = 280;
    const margin = { top: 20, right: 20, bottom: 30, left: 50 };
    const plotWidth = chartWidth - margin.left - margin.right;
    const plotHeight = chartHeight - margin.top - margin.bottom;
    const xForIndex = (index: number) => margin.left + (index * plotWidth) / (heroMilestones.length - 1);
    const yForValue = (value: number) => margin.top + (1 - value / maxFleet) * plotHeight;

    const evPoints = heroMilestones.map((point, index) => `${xForIndex(index)},${yForValue(point.ev)}`).join(" ");
    const gasPoints = heroMilestones.map((point, index) => `${xForIndex(index)},${yForValue(point.gas)}`).join(" ");

    const evArea = [
        `${xForIndex(0)},${yForValue(0)}`,
        ...heroMilestones.map((point, index) => `${xForIndex(index)},${yForValue(point.ev)}`),
        `${xForIndex(heroMilestones.length - 1)},${yForValue(0)}`,
    ].join(" ");

    const gasArea = [
        `${xForIndex(0)},${yForValue(0)}`,
        ...heroMilestones.map((point, index) => `${xForIndex(index)},${yForValue(point.gas)}`),
        `${xForIndex(heroMilestones.length - 1)},${yForValue(0)}`,
    ].join(" ");

    const yTicks = [0, Math.round(maxFleet * 0.25), Math.round(maxFleet * 0.5), Math.round(maxFleet * 0.75), maxFleet];
    const phaseIndexes = [1, 2, 3, 4, 5];
    const lastIndex = heroMilestones.length - 1;
    const lastEvX = xForIndex(lastIndex);
    const lastEvY = yForValue(heroMilestones[lastIndex].ev);

    const handleChartMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const svgX = ((event.clientX - rect.left) / rect.width) * chartWidth;
        const clampedX = Math.max(margin.left, Math.min(svgX, chartWidth - margin.right));
        const stepWidth = plotWidth / (heroMilestones.length - 1);
        const nextIndex = Math.max(0, Math.min(heroMilestones.length - 1, Math.round((clampedX - margin.left) / stepWidth)));

        setHoveredIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    };

    const tooltip = (() => {
        if (hoveredIndex === null) {
            return null;
        }

        const point = heroMilestones[hoveredIndex];
        const prevEv = hoveredIndex > 0 ? heroMilestones[hoveredIndex - 1].ev : 0;
        const delta = point.ev - prevEv;
        const x = xForIndex(hoveredIndex);
        const y = yForValue(point.ev);
        const width = 168;
        const height = 98;
        const tx = Math.max(margin.left + 8, Math.min(x - width / 2, chartWidth - margin.right - width - 8));
        const ty = Math.max(12, y - height - 18);

        return {
            point,
            delta,
            tx,
            ty,
            width,
            height,
        };
    })();

    return (
        <div className="relative rounded-[28px] border border-white/10 bg-white/10 px-7 pt-8 pb-5 backdrop-blur-[10px]">
            <p className="mb-4 text-center text-[14px] font-bold tracking-[1px] text-lime-300 uppercase" style={{ fontFamily: "var(--font-dm-mono)" }}>Fleet Decarbonization Milestones</p>
            <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="mt-4 h-auto w-full"
                onMouseMove={handleChartMouseMove}
                onMouseLeave={() => setHoveredIndex(null)}
            >
                <defs>
                    <linearGradient id="ev-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgb(52 211 153)" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="rgb(52 211 153)" stopOpacity="0.04" />
                    </linearGradient>
                    <linearGradient id="gas-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgb(251 113 133)" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="rgb(251 113 133)" stopOpacity="0.03" />
                    </linearGradient>
                    <filter id="line-glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="2.8" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {yTicks.map((tick) => (
                    <g key={tick}>
                        <line x1={margin.left} y1={yForValue(tick)} x2={chartWidth - margin.right} y2={yForValue(tick)} stroke="rgba(255,255,255,0.08)" strokeDasharray="4" />
                        <text x={margin.left - 8} y={yForValue(tick) + 4} fill="rgba(255,255,255,0.4)" fontSize="10" textAnchor="end" style={{ fontFamily: "var(--font-dm-mono)" }}>
                            {tick}
                        </text>
                    </g>
                ))}

                <polygon points={gasArea} fill="url(#gas-fill)" />
                <polygon points={evArea} fill="url(#ev-fill)" />

                <polyline points={gasPoints} fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                <polyline
                    points={evPoints}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#line-glow)"
                />

                <g className="pointer-events-none text-emerald-300">
                    <circle cx={lastEvX} cy={lastEvY} r="7" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.45">
                        <animate attributeName="r" values="7;15;7" dur="2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.45;0;0.45" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={lastEvX} cy={lastEvY} r="10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.28">
                        <animate attributeName="r" values="10;18;10" dur="2s" begin="0.6s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.28;0;0.28" dur="2s" begin="0.6s" repeatCount="indefinite" />
                    </circle>
                </g>

                {heroMilestones.map((point, index) => (
                    <g key={point.year}>
                        <circle
                            cx={xForIndex(index)}
                            cy={yForValue(point.gas)}
                            r="4"
                            fill="#0b3d2e"
                            stroke="#ef4444"
                            strokeWidth="2"
                            onMouseEnter={() => setHoveredIndex(index)}
                        />
                        <circle
                            cx={xForIndex(index)}
                            cy={yForValue(point.ev)}
                            r="5"
                            fill="#0b3d2e"
                            stroke="#10b981"
                            strokeWidth="2.5"
                            onMouseEnter={() => setHoveredIndex(index)}
                        />
                        <text x={xForIndex(index)} y={chartHeight - 6} fill="rgba(255,255,255,0.5)" fontSize="10" fontWeight="600" textAnchor="middle" style={{ fontFamily: "var(--font-dm-mono)" }}>
                            {point.year}
                        </text>
                    </g>
                ))}

                {phaseIndexes.map((index) => {
                    const phase = heroMilestones[index];
                    return (
                        <text
                            key={phase.year}
                            x={xForIndex(index) - 10}
                            y={yForValue(phase.ev) - 14}
                            fill="rgba(255,255,255,0.65)"
                            fontSize="10"
                            fontWeight="600"
                            style={{ fontFamily: "var(--font-dm-mono)" }}
                            textAnchor="end"
                        >
                            {`${phase.label}: +${phase.ev - heroMilestones[index - 1].ev} EVs`}
                        </text>
                    );
                })}

                <text x={chartWidth - margin.right - 4} y={yForValue(maxFleet) - 10} fill="#84cc16" fontSize="10" fontWeight="700" textAnchor="end" style={{ fontFamily: "var(--font-dm-mono)" }}>
                    Net Zero
                </text>

                <g transform={`translate(${margin.left + 10}, ${chartHeight - 22})`}>
                    <circle cx="0" cy="0" r="5" fill="none" stroke="#10b981" strokeWidth="2" />
                    <text x="10" y="4" fill="rgba(255,255,255,0.6)" fontSize="10">
                        BEV Fleet (cumulative)
                    </text>
                    <circle cx="180" cy="0" r="5" fill="none" stroke="#ef4444" strokeWidth="2" />
                    <text x="190" y="4" fill="rgba(255,255,255,0.6)" fontSize="10">
                        Diesel In-Use
                    </text>
                </g>

                {tooltip && (
                    <g>
                        <rect x={tooltip.tx} y={tooltip.ty} width={tooltip.width} height={tooltip.height} rx="10" fill="rgba(0,0,0,0.85)" />
                        <text x={tooltip.tx + 14} y={tooltip.ty + 24} fill="#84cc16" fontSize="14" fontWeight="700">
                            {tooltip.point.year}
                        </text>

                        <circle cx={tooltip.tx + 18} cy={tooltip.ty + 44} r="4" fill="#10b981" />
                        <text x={tooltip.tx + 28} y={tooltip.ty + 48} fill="#fff" fontSize="12">
                            BEV Fleet:
                        </text>
                        <text x={tooltip.tx + 86} y={tooltip.ty + 48} fill="#fff" fontSize="12" fontWeight="700">
                            {tooltip.point.ev}
                        </text>

                        <circle cx={tooltip.tx + 18} cy={tooltip.ty + 64} r="4" fill="#ef4444" />
                        <text x={tooltip.tx + 28} y={tooltip.ty + 68} fill="#fff" fontSize="12">
                            Diesel:
                        </text>
                        <text x={tooltip.tx + 86} y={tooltip.ty + 68} fill="#fff" fontSize="12" fontWeight="700">
                            {tooltip.point.gas}
                        </text>

                        {tooltip.point.label !== "Baseline" && (
                            <text x={tooltip.tx + 14} y={tooltip.ty + 86} fill="#84cc16" fontSize="11">
                                {`${tooltip.point.label}: +${tooltip.delta} EVs`}
                            </text>
                        )}
                    </g>
                )}
            </svg>
            <p className="mt-2 text-center text-[12px] text-white/50">Trekker fleet • BEV transition 2025-2032</p>
        </div>
    );
}
